import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer,
  BarChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  Cell,
} from 'recharts';
import { MdDashboard } from 'react-icons/md';
import {
  FiDroplet,
  FiTruck,
  FiActivity,
  FiMap,
  FiTrendingUp,
  FiAlertCircle,
  FiThermometer,
  FiAlertTriangle,
  FiCheck,
  FiChevronLeft,
  FiChevronRight,
} from 'react-icons/fi';
import { usePageContext } from '../../../../context/usePage';

import {
  PageContainer,
  Header,
  KpiGrid,
  KpiCard,
  LoadingOverlay,
  ChartContainerWrapper,
  DashboardGrid,
  Span2Chart,
  Span1Chart,
  TableWrapper,
  StyledTable,
  Badge,
  PaginationWrapper,
  PageButton,
} from './styles';

import { LoadingData } from '../../../../components/LoadingData';
import { useDashboardData } from './useDashboardData';
import {
  formatNumber,
  formatName,
  formatCompactNumber,
  STATUS_CONFIG,
} from './utils';

export const ConferenciaProdutor = () => {
  const navigate = useNavigate();
  const { version } = usePageContext();
  const {
    loading,
    resumoData,
    kpis,
    currentPage,
    setCurrentPage,
    totalPages,
    paginatedColetas,
  } = useDashboardData();

  return (
    <PageContainer>
      <Header>
        <h1>
          <MdDashboard />
          Dashboard
        </h1>
        <p>Gestão de captação.</p>
      </Header>

      {loading || !kpis ? (
        <LoadingOverlay>
          <LoadingData title="Carregando Dashboard" />
        </LoadingOverlay>
      ) : (
        <KpiGrid>
          <KpiCard $delay={0.1} $color="#2563eb">
            <div className="card-content">
              <div className="title">Lt Coletado</div>
              <div className="value">
                {formatNumber(kpis.totalLitros, 0, 'L')}
              </div>
            </div>
            <div className="icon-wrapper">
              <FiDroplet />
            </div>
          </KpiCard>

          <KpiCard $delay={0.2} $color="#8b5cf6">
            <div className="card-content">
              <div className="title">Nº de Viagens</div>
              <div className="value">{formatNumber(kpis.totalViagens)}</div>
            </div>
            <div className="icon-wrapper">
              <FiTruck />
            </div>
          </KpiCard>

          <KpiCard $delay={0.3} $color="#f59e0b">
            <div className="card-content">
              <div className="title">Média por Coleta</div>
              <div className="value">
                {formatNumber(kpis.litragemMedia, 1, 'L')}
              </div>
            </div>
            <div className="icon-wrapper">
              <FiActivity />
            </div>
          </KpiCard>

          <KpiCard $delay={0.4} $color="#10b981">
            <div className="card-content">
              <div className="title">Km Rodado</div>
              <div className="value">
                {formatNumber(kpis.kmTotalRodado, 1, 'km')}
              </div>
            </div>
            <div className="icon-wrapper">
              <FiMap />
            </div>
          </KpiCard>

          <KpiCard $delay={0.5} $color="#ec4899">
            <div className="card-content">
              <div className="title">Média por km</div>
              <div className="value">
                {formatNumber(kpis.litroPorKm, 2, 'L')}
              </div>
            </div>
            <div className="icon-wrapper">
              <FiTrendingUp />
            </div>
          </KpiCard>
        </KpiGrid>
      )}

      {!loading && kpis && kpis.chartData && (
        <DashboardGrid>
          <Span2Chart>
            <h3>Volume Total - Últimos 12 Meses</h3>
            <ResponsiveContainer debounce={100} width="100%" height={300}>
              <BarChart
                data={kpis.chartData}
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={true}
                  stroke="#e2e8f0"
                />
                <XAxis
                  dataKey="name"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
                  dy={10}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 12, fontWeight: 600 }}
                  tickFormatter={val => formatCompactNumber(val)}
                  dx={-10}
                />
                <Tooltip
                  cursor={{ fill: '#f1f5f9' }}
                  contentStyle={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    maxWidth: '220px',
                    whiteSpace: 'normal',
                  }}
                  formatter={value => [
                    `${formatNumber(Number(value))} L`,
                    'Volume Total',
                  ]}
                />
                <Bar dataKey="volume" radius={[6, 6, 0, 0]} maxBarSize={50}>
                  {kpis.chartData.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={
                        kpis.top3ChartIndexes.includes(index) &&
                        entry.volume > 0
                          ? '#0D9488'
                          : '#5EEAD4'
                      }
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Span2Chart>

          <Span1Chart>
            <h3>Fila de Descarga</h3>
            <div style={{ flex: 1, overflowY: 'auto', maxHeight: '300px' }}>
              {resumoData?.fila_descarga &&
              resumoData.fila_descarga.length > 0 ? (
                <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                  {resumoData.fila_descarga.map((item, idx) => {
                    return (
                      <li
                        key={idx}
                        onClick={() =>
                          navigate(
                            `/plataforma/${item.ID_COLETA}/acompanhar${version}`,
                          )
                        }
                        style={{
                          padding: '12px 16px',
                          background: idx % 2 === 0 ? '#f0f9ff' : '#f8fafc',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          cursor: 'pointer',
                        }}
                      >
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                          }}
                        >
                          <span
                            style={{
                              fontSize: '0.7rem',
                              color: '#64748b',
                              fontWeight: 600,
                              textTransform: 'uppercase',
                              letterSpacing: '0.5px',
                            }}
                          >
                            Coleta #{item.ID_COLETA}
                          </span>
                          <strong
                            style={{ fontSize: '0.95rem', color: '#334155' }}
                          >
                            {item.VEICULO}
                          </strong>
                          <span
                            style={{
                              fontSize: '0.8rem',
                              color: '#475569',
                              marginBottom: '2px',
                            }}
                          >
                            {item.MOTORISTA}
                          </span>
                          <span
                            style={{ fontSize: '0.75rem', color: '#64748b' }}
                          >
                            Aguardando desde{' '}
                            {item.DATA_SAIDA
                              ? moment(item.DATA_SAIDA).format('DD/MM/YYYY')
                              : '--'}{' '}
                            às {item.HORA_SAIDA?.substring(0, 5) || '--:--'}
                          </span>
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: '4px',
                          }}
                        >
                          {item.ALERTA_DESCARGA_MOTORISTA && (
                            <span
                              style={{
                                fontSize: '0.75rem',
                                color: '#f97316',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                fontWeight: 500,
                                whiteSpace: 'nowrap',
                              }}
                            >
                              <FiAlertCircle size={14} />
                              Descarga pendente
                            </span>
                          )}
                          <strong
                            style={{ fontSize: '0.9rem', color: '#2563eb' }}
                          >
                            {formatNumber(Number(item.QTD_COLETADA))} L
                          </strong>
                          <span
                            style={{
                              fontSize: '0.8rem',
                              color: '#64748b',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <FiThermometer size={14} />
                            {item.TEMPERATURA != null
                              ? `${item.TEMPERATURA} °C`
                              : '--'}
                          </span>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              ) : (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '100%',
                    color: '#94a3b8',
                    fontSize: '0.9rem',
                  }}
                >
                  Fila vazia
                </div>
              )}
            </div>
          </Span1Chart>
        </DashboardGrid>
      )}

      {!loading && resumoData && (
        <DashboardGrid>
          <ChartContainerWrapper style={{ padding: '16px 8px', margin: 0 }}>
            <h3 style={{ paddingLeft: '8px' }}>Ranking de Linhas</h3>
            <ResponsiveContainer debounce={100} width="100%" height={550}>
              <BarChart
                layout="vertical"
                data={resumoData.ranking_linhas}
                margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={true}
                  vertical={true}
                  stroke="#e2e8f0"
                />
                <XAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
                  tickFormatter={val => formatCompactNumber(val)}
                />
                <YAxis
                  dataKey="DFNOMELINHA"
                  type="category"
                  width={130}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 14, fontWeight: 500 }}
                />
                <Tooltip
                  cursor={{ fill: '#f1f5f9' }}
                  contentStyle={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    maxWidth: '220px',
                    whiteSpace: 'normal',
                  }}
                  formatter={value => [
                    `${formatNumber(Number(value))} L`,
                    'Volume Total',
                  ]}
                />
                <Bar
                  dataKey="VOLUME_TOTAL"
                  fill="#8b5cf6"
                  radius={[0, 4, 4, 0]}
                  barSize={24}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainerWrapper>

          <ChartContainerWrapper style={{ padding: '16px 8px', margin: 0 }}>
            <h3 style={{ paddingLeft: '8px' }}>Ranking de Tanques</h3>
            <ResponsiveContainer debounce={100} width="100%" height={550}>
              <BarChart
                layout="vertical"
                data={resumoData.ranking_tanques}
                margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={true}
                  vertical={true}
                  stroke="#e2e8f0"
                />
                <XAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
                  tickFormatter={val => formatCompactNumber(val)}
                />
                <YAxis
                  dataKey="DFDESCTANQUE"
                  type="category"
                  width={130}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 14, fontWeight: 500 }}
                  tickFormatter={val => {
                    if (!val) return '';
                    const parts = String(val).split(' ');
                    return parts.slice(0, 2).join(' ');
                  }}
                />
                <Tooltip
                  cursor={{ fill: '#f1f5f9' }}
                  contentStyle={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    maxWidth: '220px',
                    whiteSpace: 'normal',
                  }}
                  formatter={value => [
                    `${formatNumber(Number(value))} L`,
                    'Volume Total',
                  ]}
                />
                <Bar
                  dataKey="VOLUME_TOTAL"
                  fill="#10b981"
                  radius={[0, 4, 4, 0]}
                  barSize={24}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainerWrapper>

          <ChartContainerWrapper style={{ padding: '16px 8px', margin: 0 }}>
            <h3 style={{ paddingLeft: '8px' }}>Ranking de Produtores</h3>
            <ResponsiveContainer debounce={100} width="100%" height={550}>
              <BarChart
                layout="vertical"
                data={resumoData.ranking_produtores}
                margin={{ top: 10, right: 20, left: 10, bottom: 0 }}
              >
                <CartesianGrid
                  strokeDasharray="3 3"
                  horizontal={true}
                  vertical={true}
                  stroke="#e2e8f0"
                />
                <XAxis
                  type="number"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 11, fontWeight: 500 }}
                  tickFormatter={val => formatCompactNumber(val)}
                />
                <YAxis
                  dataKey="DFNOMEPRODUTOR"
                  type="category"
                  width={130}
                  axisLine={false}
                  tickLine={false}
                  tick={{ fill: '#64748b', fontSize: 14, fontWeight: 500 }}
                  tickFormatter={formatName}
                />
                <Tooltip
                  cursor={{ fill: '#f1f5f9' }}
                  contentStyle={{
                    borderRadius: '12px',
                    border: 'none',
                    boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
                    maxWidth: '220px',
                    whiteSpace: 'normal',
                  }}
                  formatter={value => [
                    `${formatNumber(Number(value))} L`,
                    'Volume Total',
                  ]}
                />
                <Bar
                  dataKey="VOLUME_TOTAL"
                  fill="#ec4899"
                  radius={[0, 4, 4, 0]}
                  barSize={24}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartContainerWrapper>
        </DashboardGrid>
      )}

      {!loading && resumoData && resumoData.ultimas_coletas && (
        <TableWrapper>
          <div>
            <h3>Últimas Coletas</h3>
            <StyledTable>
              <thead>
                <tr>
                  <th>Coleta</th>
                  <th>Status</th>
                  <th>Veículo</th>
                  <th>Linha</th>
                  <th>Data</th>
                  <th>Qtd. Coletada</th>
                  <th>Anomalia</th>
                  <th>Socorro</th>
                  <th>Eficiência</th>
                </tr>
              </thead>
              <tbody>
                {paginatedColetas.map((item, idx) => (
                  <tr
                    key={idx}
                    onClick={() =>
                      navigate(
                        `/plataforma/${item.ID_COLETA}/acompanhar${version}`,
                      )
                    }
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <strong>{item.ID_COLETA}</strong>
                    </td>
                    <td>
                      <Badge
                        $color={STATUS_CONFIG[item.STATUS]?.color || '#64748b'}
                      >
                        {STATUS_CONFIG[item.STATUS]?.label || item.STATUS}
                      </Badge>
                    </td>
                    <td>{item.VEICULO}</td>
                    <td>{item.LINHA}</td>
                    <td>
                      {item.DATA_COLETA
                        ? moment(item.DATA_COLETA).format('DD/MM/YYYY')
                        : '--'}
                    </td>
                    <td>
                      <strong>
                        {formatNumber(Number(item.QTD_COLETADA))} L
                      </strong>
                    </td>
                    <td>
                      {item.ANOMALIA ? (
                        <Badge
                          $color="#ef4444"
                          title={item.TEXTO_ANOMALIA || 'Sem descrição'}
                        >
                          <FiAlertTriangle />
                        </Badge>
                      ) : (
                        <Badge $color="#10b981" title="Sem anomalia">
                          <FiCheck />
                        </Badge>
                      )}
                    </td>
                    <td>
                      {item.SOCORRO ? (
                        <Badge $color="#f59e0b">Sim</Badge>
                      ) : (
                        <Badge $color="#10b981">Não</Badge>
                      )}
                    </td>
                    <td>
                      {typeof item.EFICIENCIA === 'number' ? (
                        <div
                          title={`Coletados ${
                            item.QTD_TANQUES_COLETADOS || 0
                          } de ${
                            item.QTD_TANQUES_PROGRAMADOS || 0
                          } tanques programados`}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            minWidth: '120px',
                          }}
                        >
                          <div
                            style={{
                              flex: 1,
                              background: '#e2e8f0',
                              borderRadius: '4px',
                              height: '8px',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                width: `${Math.min(item.EFICIENCIA, 100)}%`,
                                background: '#10b981',
                                height: '100%',
                                transition: 'width 0.3s ease',
                              }}
                            ></div>
                          </div>
                          <span
                            style={{
                              fontSize: '0.85rem',
                              fontWeight: 600,
                              color: '#475569',
                              minWidth: '35px',
                              textAlign: 'right',
                            }}
                          >
                            {Math.round(item.EFICIENCIA)}%
                          </span>
                        </div>
                      ) : (
                        '--'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </StyledTable>
          </div>

          {totalPages > 1 && (
            <PaginationWrapper>
              <span>
                Página <strong>{currentPage}</strong> de {totalPages}
              </span>
              <PageButton
                disabled={currentPage === 1}
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              >
                <FiChevronLeft />
              </PageButton>
              <PageButton
                disabled={currentPage === totalPages}
                onClick={() =>
                  setCurrentPage(prev => Math.min(prev + 1, totalPages))
                }
              >
                <FiChevronRight />
              </PageButton>
            </PaginationWrapper>
          )}
        </TableWrapper>
      )}
    </PageContainer>
  );
};

export default ConferenciaProdutor;
